import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { PasswordInput } from "../components/PasswordInput";
import { cx } from "../components/ui";

export type SignUpField =
  | "industry"
  | "business_name"
  | "first_name"
  | "last_name"
  | "email"
  | "password";

export type SignUpFieldErrors = Partial<Record<SignUpField, string[]>>;

export interface SignUpValues {
  industry: string;
  business_name: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
}

const SIGN_UP_FIELDS: SignUpField[] = [
  "industry",
  "business_name",
  "first_name",
  "last_name",
  "email",
  "password",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_LENGTHS: Partial<Record<SignUpField, number>> = {
  business_name: 160,
  first_name: 96,
  last_name: 96,
  email: 254,
};

/**
 * Company registration: the supported self-serve door.
 *
 * One screen — industry, business name, owner name, email and password — which
 * the server turns into a new tenant and its Owner in one transaction
 * (`POST /auth/signup`). It is the same provisioning path the platform uses, so
 * a self-serve account and a platform-created account are identical.
 *
 * The new account is provisioned on the plan the server chooses (currently the
 * Free plan). Registration does not grant modules the plan does not include;
 * what the account can use is decided by the plan, not by this form.
 */
export function SignUpPage() {
  const { signUp } = useAuth();
  const [businessName, setBusinessName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [industry, setIndustry] = useState("");
  const [clientErrors, setClientErrors] = useState<SignUpFieldErrors>({});
  const [serverErrors, setServerErrors] = useState<SignUpFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const industries = useQuery({
    queryKey: ["industries"],
    queryFn: ({ signal }) => api.industries(signal),
    staleTime: 5 * 60_000,
  });

  const values: SignUpValues = {
    industry,
    business_name: businessName.trim(),
    first_name: firstName.trim(),
    last_name: lastName.trim(),
    email: email.trim(),
    password,
  };
  const fieldErrors = mergeSignUpErrors(clientErrors, serverErrors);

  function clearFieldError(field: SignUpField) {
    setClientErrors((current) => omitField(current, field));
    setServerErrors((current) => omitField(current, field));
    setFormError(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    const problems = validateSignUp(values);
    if (Object.keys(problems).length > 0) {
      setClientErrors(problems);
      setServerErrors({});
      setFormError(null);
      return;
    }

    setClientErrors({});
    setServerErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      await signUp({
        industry: values.industry,
        business_name: values.business_name,
        first_name: values.first_name || undefined,
        last_name: values.last_name,
        email: values.email,
        password: values.password,
      });
    } catch (caught) {
      const apiError =
        caught instanceof ApiError
          ? caught
          : new ApiError({ code: "network", message: "Could not reach the server.", status: 0 });
      const mapped = signUpServerFieldErrors(apiError.fields);
      setServerErrors(mapped);
      setFormError(signUpGeneralError(apiError, mapped));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-slate-900 p-10 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex size-8 items-center justify-center rounded-md bg-white text-sm font-semibold text-slate-900">
            S
          </div>
          <span className="text-sm font-semibold">SBM One</span>
        </div>

        <div className="max-w-sm">
          <h1 className="text-2xl font-semibold leading-tight tracking-tight">
            Set up your business in one screen.
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            Create your company and your Owner account. Pick your industry and the
            product arrives tuned to it — no consultant, no long setup.
          </p>
        </div>

        <ul className="space-y-3 text-sm text-slate-300">
          {[
            ["One tenant", "Your records are isolated to your company from the first request."],
            ["One Owner", "You sign in immediately and invite the rest of your team."],
            ["One source of truth", "Customers, people and billing share the same data."],
          ].map(([title, body]) => (
            <li key={title}>
              <span className="font-medium text-white">{title}</span> — {body}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex size-8 items-center justify-center rounded-md bg-slate-900 text-sm font-semibold text-white">
              S
            </div>
            <span className="text-sm font-semibold text-slate-900">SBM One</span>
          </div>

          <h2 className="text-xl font-semibold tracking-tight text-slate-900">Create your account</h2>
          <p className="mt-1 text-sm text-slate-500">
            Business name, your details, and a password. Takes a minute.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-8 space-y-4">
            <div>
              <label htmlFor="industry" className="block text-sm font-medium text-slate-700">
                Industry
              </label>
              <select
                id="industry"
                name="industry"
                required
                value={industry}
                onChange={(event) => {
                  setIndustry(event.target.value);
                  clearFieldError("industry");
                }}
                aria-invalid={fieldErrors.industry?.length ? true : undefined}
                aria-describedby={fieldErrors.industry?.length ? "industry-error" : undefined}
                className={cx(
                  "mt-1 w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-slate-900 ring-1 ring-inset focus:ring-2 focus:ring-inset",
                  fieldErrors.industry?.length
                    ? "ring-rose-500 focus:ring-rose-500"
                    : "ring-slate-300 focus:ring-slate-900",
                )}
              >
                <option value="">
                  {industries.isPending ? "Loading…" : "Choose your industry"}
                </option>
                {(industries.data ?? []).map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
              <FieldErrors errors={fieldErrors.industry} id="industry" />
            </div>

            <SignUpField
              id="business_name"
              label="Business name"
              type="text"
              autoComplete="organization"
              required
              value={businessName}
              onChange={(value) => {
                setBusinessName(value);
                clearFieldError("business_name");
              }}
              errors={fieldErrors.business_name}
            />
            <div className="grid grid-cols-2 gap-3">
              <SignUpField
                id="first_name"
                label="First name"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(value) => {
                  setFirstName(value);
                  clearFieldError("first_name");
                }}
                errors={fieldErrors.first_name}
              />
              <SignUpField
                id="last_name"
                label="Last name"
                type="text"
                autoComplete="family-name"
                required
                value={lastName}
                onChange={(value) => {
                  setLastName(value);
                  clearFieldError("last_name");
                }}
                errors={fieldErrors.last_name}
              />
            </div>
            <SignUpField
              id="email"
              label="Work email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(value) => {
                setEmail(value);
                clearFieldError("email");
              }}
              errors={fieldErrors.email}
            />
            <SignUpField
              id="password"
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(value) => {
                setPassword(value);
                clearFieldError("password");
              }}
              errors={fieldErrors.password}
              hint="At least 10 characters, with letters and numbers."
            />

            {formError ? (
              <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {formError}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-slate-900 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? "Creating your account…" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/signin" className="font-medium text-slate-900 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function validateSignUp(values: SignUpValues): SignUpFieldErrors {
  const errors: SignUpFieldErrors = {};
  const businessName = values.business_name.trim();
  const firstName = values.first_name.trim();
  const lastName = values.last_name.trim();
  const email = values.email.trim();

  if (!values.industry) {
    errors.industry = ["Choose an industry."];
  }
  if (!businessName) {
    errors.business_name = ["Enter your business name."];
  } else if (businessName.length > MAX_LENGTHS.business_name!) {
    errors.business_name = ["Business name must be 160 characters or fewer."];
  }
  if (firstName.length > MAX_LENGTHS.first_name!) {
    errors.first_name = ["First name must be 96 characters or fewer."];
  }
  if (!lastName) {
    errors.last_name = ["Enter your last name."];
  } else if (lastName.length > MAX_LENGTHS.last_name!) {
    errors.last_name = ["Last name must be 96 characters or fewer."];
  }
  if (!email) {
    errors.email = ["Enter your work email."];
  } else if (email.length > MAX_LENGTHS.email!) {
    errors.email = ["Email must be 254 characters or fewer."];
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = ["Enter a valid email address."];
  }
  if (!values.password) {
    errors.password = ["Enter a password."];
  } else if (values.password.length < 10) {
    errors.password = ["Use at least 10 characters."];
  } else if (values.password.match(/^\d+$/) || values.password.match(/^[A-Za-z]+$/)) {
    errors.password = ["Use a mix of letters and numbers."];
  }

  return errors;
}

export function signUpServerFieldErrors(
  fields: Record<string, string[]> | undefined,
): SignUpFieldErrors {
  const errors: SignUpFieldErrors = {};
  for (const field of SIGN_UP_FIELDS) {
    const messages = fields?.[field];
    if (messages?.length) errors[field] = messages;
  }
  return errors;
}

export function mergeSignUpErrors(
  ...sources: Array<SignUpFieldErrors | null | undefined>
): SignUpFieldErrors {
  const errors: SignUpFieldErrors = {};
  for (const field of SIGN_UP_FIELDS) {
    const messages = sources.flatMap((source) => source?.[field] ?? []);
    if (messages.length > 0) errors[field] = messages;
  }
  return errors;
}

export function signUpGeneralError(
  error: ApiError | null,
  mapped: SignUpFieldErrors,
): string | null {
  if (!error) return null;

  const unhandled = Object.entries(error.fields)
    .filter(([field]) => !SIGN_UP_FIELDS.includes(field as SignUpField))
    .flatMap(([, messages]) => messages);
  if (unhandled.length > 0) return unhandled.join(" ");
  if (Object.keys(mapped).length > 0) return null;
  return error.message;
}

function omitField(errors: SignUpFieldErrors, field: SignUpField): SignUpFieldErrors {
  if (!errors[field]) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}

interface FieldProps {
  id: string;
  label: string;
  type: string;
  autoComplete: string;
  required?: boolean | undefined;
  value: string;
  onChange: (value: string) => void;
  errors?: string[] | undefined;
  hint?: string | undefined;
}

export function SignUpField({
  id,
  label,
  type,
  autoComplete,
  required,
  value,
  onChange,
  errors,
  hint,
}: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      {type === "password" ? (
        <PasswordInput
          id={id}
          autoComplete={autoComplete}
          required={required}
          value={value}
          onChange={onChange}
          invalid={Boolean(errors?.length)}
          aria-invalid={errors?.length ? true : undefined}
          aria-describedby={errors?.length ? `${id}-error` : undefined}
          className="mt-1"
        />
      ) : (
        <input
          id={id}
          name={id}
          type={type}
          autoComplete={autoComplete}
          required={required}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={errors?.length ? true : undefined}
          aria-describedby={errors?.length ? `${id}-error` : undefined}
          className={cx(
            "mt-1 w-full rounded-md border-0 px-3 py-2 text-sm text-slate-900 ring-1 ring-inset placeholder:text-slate-400 focus:ring-2 focus:ring-inset",
            errors?.length
              ? "ring-rose-500 focus:ring-rose-500"
              : "ring-slate-300 focus:ring-slate-900",
          )}
        />
      )}
      {hint && !errors?.length ? <p className="mt-1 text-xs text-slate-400">{hint}</p> : null}
      <FieldErrors errors={errors} id={id} />
    </div>
  );
}

function FieldErrors({ errors, id }: { errors?: string[] | undefined; id: string }) {
  if (!errors?.length) return null;
  return (
    <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-rose-600">
      {errors.join(" ")}
    </p>
  );
}
